// src/pages/Products/ProductDetail.tsx

import { Card, Descriptions, Button, Space, message, Modal, Tag } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { getProduct, deleteProduct } from '@/api/products';
import { getPanel } from '@/api/panels'; // 导入获取面板的 API
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { getCurrentDeveloperId } from '@/stores/authStore';
// 导入配套图标
import { 
  ArrowLeftOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  WarningOutlined, 
  InfoCircleOutlined, 
  AppleOutlined, 
  AndroidOutlined, 
  PictureOutlined,
  CloudUploadOutlined // ✅ 新增：OTA 固件图标
} from '@ant-design/icons';

/**
产品详情页面
*/
export const ProductDetail = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const developerId = getCurrentDeveloperId();

  // 查询产品详情
  const productQuery = useQuery({
    queryKey: ['product', developerId, productId],
    queryFn: () => {
      if (!productId) {
        throw new Error('产品 ID 不能为空');
      }
      return getProduct(productId);
    },
    enabled: !!productId && !!developerId,
  });

  // 获取关联的 iOS 面板详情
  const iosPanelQuery = useQuery({
    queryKey: ['panel', developerId, productQuery.data?.ios_panel_id],
    queryFn: () => {
      if (!productQuery.data?.ios_panel_id) {
        throw new Error('iOS 面板 ID 不存在');
      }
      return getPanel(productQuery.data.ios_panel_id);
    },
    enabled: !!productQuery.data?.ios_panel_id && !!developerId,
    retry: 1,
  });

  // 获取关联的 Android 面板详情
  const androidPanelQuery = useQuery({
    queryKey: ['panel', developerId, productQuery.data?.android_panel_id],
    queryFn: () => {
      if (!productQuery.data?.android_panel_id) {
        throw new Error('Android 面板 ID 不存在');
      }
      return getPanel(productQuery.data.android_panel_id);
    },
    enabled: !!productQuery.data?.android_panel_id && !!developerId,
    retry: 1,
  });

  const product = productQuery.data;
  const iosPanel = iosPanelQuery.data;
  const androidPanel = androidPanelQuery.data;
  const loading = productQuery.isLoading || iosPanelQuery.isLoading || androidPanelQuery.isLoading;
  const error = productQuery.error || iosPanelQuery.error || androidPanelQuery.error;

  /**
   处理编辑产品
   */
  const handleEdit = (): void => {
    if (productId) {
      navigate(`/products/${productId}/edit`);
    }
  };

  /**
   处理删除产品
   */
  const handleDelete = (): void => {
    if (!productId || !product) return;
    Modal.confirm({
      title: '确认删除产品',
      icon: <WarningOutlined className="text-orange-500" />,
      content: `确定要删除产品 "${product.name}" 吗？此操作不可恢复，已集成的 App 将无法使用该产品功能！`,
      okType: 'danger',
      okText: '确认删除',
      cancelText: '取消',
      onOk: async () => {
        try {
          await deleteProduct(productId);
          message.success('产品删除成功');
          // 清除相关的缓存
          await Promise.allSettled([
            queryClient.invalidateQueries({ queryKey: ['products', developerId] }),
            queryClient.invalidateQueries({ queryKey: ['all-products', developerId] }),
            queryClient.invalidateQueries({ queryKey: ['products'] }),
            // 如果 App 相关的缓存也需要更新
            queryClient.invalidateQueries({ queryKey: ['app-integrated-products'] }),
          ]);
          navigate('/products');
        } catch {
          message.error('请确保该产品未被任何 App 集成、及无图片及无 OTA 固件后重试');
        }
      },
    });
  };

  /**
   处理返回列表
   */
  const handleBack = (): void => {
    navigate('/products');
  };

  /**
   处理图片管理
   */
  const handleManageImages = (): void => {
    if (productId) {
      navigate(`/products/${productId}/images`);
    }
  };

  /**
   处理 OTA 固件管理 ✅ 新增
   */
  const handleManageOTA = (): void => {
    if (productId) {
      navigate(`/products/${productId}/ota`);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <ErrorAlert 
        message="加载失败" 
        description="无法加载产品详情，请检查网络或稍后重试。" 
        className="mb-8" 
      />
    );
  }

  if (!product) {
    return (
      <ErrorAlert 
        message="产品不存在" 
        description="该产品可能已被删除或您无权访问。" 
        className="mb-8" 
      />
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* 标题 + 操作区 - 适配小屏 */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <InfoCircleOutlined className="text-blue-500" />
            产品详情
          </h1>
          <p className="text-gray-600 mt-1">
            查看 {product.name} 的详细配置信息，支持编辑、图片管理、OTA 固件和删除操作。
          </p>
        </div>
        <Space size="middle" wrap>
          <Button
            onClick={handleBack}
            icon={<ArrowLeftOutlined />}
            className="hover:bg-gray-50 transition-colors"
          >
            返回列表
          </Button>
          <Button
            type="primary"
            onClick={handleEdit}
            icon={<EditOutlined />}
            className="px-6 hover:shadow-md transition-shadow"
          >
            编辑产品
          </Button>
          <Button
            icon={<PictureOutlined />}
            onClick={handleManageImages}
            className="px-6 hover:shadow-md transition-shadow"
          >
            图片管理
          </Button>
          {/* ✅ 新增：OTA 固件管理按钮 */}
          <Button
            icon={<CloudUploadOutlined />}
            onClick={handleManageOTA}
            className="px-6 hover:shadow-md transition-shadow"
          >
            OTA 固件
          </Button>
          <Button
            danger
            onClick={handleDelete}
            icon={<DeleteOutlined />}
            className="px-6 hover:shadow-md transition-shadow"
          >
            删除产品
          </Button>
        </Space>
      </div>

      {/* 基本信息卡片 */}
      <Card 
        className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300 max-w-3xl"
        style={{ 
          padding: '24px',
          '--ant-card-head-font-size': '18px',
          '--ant-card-head-font-weight': 600,
          '--ant-card-head-color': '#1D2129'
        } as React.CSSProperties}
      >
        <Descriptions
          column={1}
          bordered={false}
          labelStyle={{ width: '140px', fontWeight: 600, color: '#667085' }}
          contentStyle={{ color: '#1D2129' }}
          style={{
            '--ant-descriptions-item-margin-bottom': '16px',
            fontSize: '15px'
          } as React.CSSProperties}
        >
          <Descriptions.Item label="产品名称">{product.name}</Descriptions.Item>
          <Descriptions.Item label="产品 ID">
            <span className="font-mono">{product.id}</span>
          </Descriptions.Item>
          <Descriptions.Item label="产品类别">
            <Tag color="blue">{product.category?.name || <span className="text-gray-400">未分类</span>}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="类别 ID">
            {product.category_id || <span className="text-gray-400">—</span>}
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">
            {new Date(product.created_at).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="更新时间">
            {new Date(product.updated_at).toLocaleString()}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <br />

      {/* 关联面板卡片 - 有数据才显示 */}
      {(product.ios_panel_id || product.android_panel_id) && (
        <Card 
          title="关联面板配置"
          className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300 max-w-3xl"
          style={{ 
            padding: '24px',
            '--ant-card-head-font-size': '16px',
            '--ant-card-head-font-weight': 600
          } as React.CSSProperties}
        >
          <div className="space-y-6">
            {product.ios_panel_id && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-center gap-2 mb-3">
                  <AppleOutlined className="text-blue-500 text-lg" />
                  <span className="font-semibold text-blue-700">iOS 面板配置</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <div className="text-xs text-gray-500">面板 ID</div>
                    <div className="font-mono text-sm break-all">{product.ios_panel_id}</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs text-gray-500">面板名称</div>
                    <div className="text-sm">
                      {iosPanel ? (
                        <span className="font-medium">{iosPanel.name}</span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs text-gray-500">面板版本</div>
                    <div className="text-sm">
                      {iosPanel ? (
                        <Tag color="purple" className="text-sm m-0 px-2 py-0.5">
                          v{iosPanel.version}
                        </Tag>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs text-gray-500">文件类型</div>
                    <div className="text-sm">
                      <Tag color="blue" className="text-sm m-0 px-2 py-0.5">
                        .jsbundle
                      </Tag>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {product.android_panel_id && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                <div className="flex items-center gap-2 mb-3">
                  <AndroidOutlined className="text-green-500 text-lg" />
                  <span className="font-semibold text-green-700">Android 面板配置</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <div className="text-xs text-gray-500">面板 ID</div>
                    <div className="font-mono text-sm break-all">{product.android_panel_id}</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs text-gray-500">面板名称</div>
                    <div className="text-sm">
                      {androidPanel ? (
                        <span className="font-medium">{androidPanel.name}</span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs text-gray-500">面板版本</div>
                    <div className="text-sm">
                      {androidPanel ? (
                        <Tag color="purple" className="text-sm m-0 px-2 py-0.5">
                          v{androidPanel.version}
                        </Tag>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs text-gray-500">文件类型</div>
                    <div className="text-sm">
                      <Tag color="green" className="text-sm m-0 px-2 py-0.5">
                        .bundle
                      </Tag>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};